import { useState } from "react";
import { Package, Search, Loader2, Truck, CreditCard, FileText } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useSEO } from "@/hooks/useSEO";
import { API } from "@/lib/api";
import SHOP from "@/config/shop";

const STATUS_MAP = {
  pending: { label: 'Ceka na zpracovani', color: 'bg-amber-100 text-amber-700' },
  confirmed: { label: 'Potvrzena', color: 'bg-blue-100 text-blue-700' },
  processing: { label: 'Zpracovava se', color: 'bg-indigo-100 text-indigo-700' },
  shipped: { label: 'Odeslana', color: 'bg-purple-100 text-purple-700' },
  delivered: { label: 'Dorucena', color: 'bg-emerald-100 text-emerald-700' },
  cancelled: { label: 'Zrusena', color: 'bg-red-100 text-red-700' },
};

export default function OrderCheckPage() {
  const [orderNumber, setOrderNumber] = useState('');
  const [email, setEmail] = useState('');
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  useSEO('Sledovani objednavky', `Zkontrolujte stav vasi objednavky na ${SHOP.domain}`);

  const handleCheck = async (e) => {
    e.preventDefault();
    if (!orderNumber.trim() || !email.trim()) return;
    setLoading(true); setError(''); setOrder(null);
    try {
      const res = await axios.get(`${API}/orders/check`, { params: { order_number: orderNumber.trim(), email: email.trim() } });
      setOrder(res.data);
    } catch (err) {
      setError(err.response?.status === 404 ? 'Objednavka nenalezena. Zkontrolujte cislo a email.' : 'Chyba pri hledani objednavky.');
    } finally { setLoading(false); }
  };

  const status = order ? STATUS_MAP[order.status] || { label: order.status, color: 'bg-slate-100 text-slate-700' } : null;

  return (
    <div className="max-w-2xl mx-auto px-4 py-8 sm:py-12" data-testid="order-check-page">
      <div className="text-center mb-8">
        <Package className="w-12 h-12 text-[#004E98] mx-auto mb-4" />
        <h1 className="text-2xl sm:text-3xl font-bold text-slate-800 mb-2">Sledovani objednavky</h1>
        <p className="text-slate-500">Zadejte cislo objednavky a email pro zobrazeni stavu.</p>
      </div>

      <form onSubmit={handleCheck} className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="grid sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Cislo objednavky *</label>
            <input type="text" value={orderNumber} onChange={e => setOrderNumber(e.target.value)} placeholder="OB-000001"
              className="w-full h-11 border border-slate-300 rounded px-4 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="order-number-input" required />
          </div>
          <div>
            <label className="text-xs font-bold uppercase text-slate-500 mb-1 block">Email *</label>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="vas@email.cz"
              className="w-full h-11 border border-slate-300 rounded px-4 text-sm focus:border-[#004E98] focus:ring-1 focus:ring-[#004E98] outline-none" data-testid="order-email-input" required />
          </div>
        </div>
        <button type="submit" disabled={loading} className="w-full h-11 bg-[#004E98] text-white rounded font-medium flex items-center justify-center gap-2 hover:bg-[#003B73] disabled:opacity-50" data-testid="order-check-submit">
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />} Vyhledat objednavku
        </button>
        {error && <p className="text-red-500 text-sm mt-3 text-center" data-testid="order-check-error">{error}</p>}
      </form>

      {order && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden" data-testid="order-check-result">
          <div className="p-6 border-b border-slate-200">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-bold text-slate-800">Objednavka {order.order_number}</h2>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>{status.label}</span>
            </div>
            <p className="text-sm text-slate-500">Datum: {new Date(order.created_at).toLocaleDateString('cs-CZ')}</p>
          </div>

          {order.tracking_number && (
            <div className="px-6 py-4 bg-blue-50 border-b border-blue-100 flex items-center gap-3">
              <Truck className="w-5 h-5 text-blue-600" />
              <div>
                <p className="text-sm font-medium text-blue-800">Sledovaci cislo: {order.tracking_number}</p>
                {order.tracking_url && <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">Sledovat zasilku</a>}
              </div>
            </div>
          )}

          <div className="p-6">
            <h3 className="text-sm font-bold uppercase text-slate-500 mb-3">Polozky</h3>
            {order.items?.map((item, i) => (
              <div key={i} className="flex justify-between py-2 border-b border-slate-100 last:border-0">
                <div><span className="text-sm">{item.name}</span> <span className="text-xs text-slate-400">x{item.quantity}</span></div>
                <span className="text-sm font-medium">{item.total?.toFixed(2)} Kc</span>
              </div>
            ))}
            <div className="mt-4 pt-4 border-t border-slate-200 space-y-1">
              <div className="flex justify-between text-sm"><span>Mezisoucet:</span><span>{order.subtotal?.toFixed(2)} Kc</span></div>
              {order.discount > 0 && <div className="flex justify-between text-sm text-red-600"><span>Sleva:</span><span>-{order.discount?.toFixed(2)} Kc</span></div>}
              <div className="flex justify-between text-sm"><span>Doprava:</span><span>{order.shipping_cost?.toFixed(2)} Kc</span></div>
              <div className="flex justify-between text-lg font-bold text-[#004E98] pt-2 border-t border-slate-200"><span>Celkem:</span><span>{order.total?.toFixed(2)} Kc</span></div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
